import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { ColumnConfig, ColumnPreset } from '../types';
import { SortableColumnItem } from './sortable-column-item';

interface ColumnCustomizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnConfigs: ColumnConfig[];
  onReorderColumns: (activeId: string, overId: string) => void;
  onToggleColumnVisibility: (columnId: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onReset: () => void;
  onRollback: () => void;
  onApplyPreset: (columnIds: string[]) => void;
  presets?: ColumnPreset[];
}

export function ColumnCustomizationDialog({
  open,
  onOpenChange,
  columnConfigs,
  onReorderColumns,
  onToggleColumnVisibility,
  onSave,
  onCancel,
  onReset,
  onApplyPreset,
  presets = []
}: ColumnCustomizationDialogProps) {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id && over?.id && active.id !== over.id) {
      onReorderColumns(String(active.id), String(over.id));
    }
  };

  const visibleCount = columnConfigs.filter((c) => c.visible).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="px-8 pb-4 relative bg-white">
          <div className="flex items-center gap-5">
            <DialogTitle className="text-2xl font-bold text-gray-900 tracking-tight">
              {t('common.customizeColumns', 'Customize columns')}
            </DialogTitle>
            <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100 border-none px-3 py-1 rounded-full text-xs font-semibold">
              {visibleCount} / {columnConfigs.length} {t('common.visible', 'visible')}
            </Badge>
          </div>
          <p className="text-sm text-gray-400 mt-4 font-medium">{t('common.dragToReorder', 'Drag to reorder')}</p>
          <div className="h-[1px] bg-gray-100 w-full mt-4" />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-8 py-0 custom-scrollbar bg-white">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={columnConfigs.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-0 pb-4">
                {columnConfigs.map((column) => (
                  <SortableColumnItem key={column.id} column={column} onToggle={onToggleColumnVisibility} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter className="flex flex-row flex-wrap px-8 pt-6 border-t items-center bg-white">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 flex-wrap">
              {presets.map((preset) => (
                <Button
                  key={preset.id}
                  variant="ghost"
                  className="text-[#007dba] hover:text-[#005fa3] hover:bg-transparent p-0 h-auto font-semibold text-sm"
                  onClick={() => onApplyPreset(preset.columnIds)}>
                  {preset.label}
                </Button>
              ))}
              <Button
                variant="ghost"
                className="text-gray-600 hover:text-gray-900 hover:bg-transparent p-0 h-auto font-semibold text-sm"
                onClick={onReset}>
                {t('common.resetToDefault', 'Reset to default')}
              </Button>
            </div>
          </div>

          <div className="flex items-center ml-auto gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              className="h-10 px-6 border-gray-200 rounded-lg text-sm font-bold text-gray-700 hover:bg-gray-50">
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={onSave}
              className="h-10 px-6 bg-[#007dba] hover:bg-[#005fa3] rounded-lg text-sm font-bold text-white shadow-sm transition-all active:scale-[0.98]">
              {t('common.apply', 'Apply')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
